import { Component, ChangeDetectorRef } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from "@angular/forms";
import { Subscription, take } from "rxjs";
import { IUser } from "src/app/shared/auth/auth.model";
import { AuthenticationService } from "src/app/shared/auth/auth.service";
import { MyProfileService } from "../../services/my-profile.service";
import { LookupsService } from "../../services/lookups.service";
import { MatCardModule } from "@angular/material/card";
import { MatTableModule } from "@angular/material/table";
import { MatButtonModule } from "@angular/material/button";
import { MatExpansionModule } from "@angular/material/expansion";
import { MyProfileContactInformationComponent } from "./profile-panels/my-profile-contact-information/my-profile-contact-information.component";
import { CommonModule } from "@angular/common";
import { MyProfileAgreementComponent } from "./profile-panels/my-profile-agreement/my-profile-agreement.component";
import { MyProfileCompanyInformationComponent } from "./profile-panels/my-profile-company-information/my-profile-company-information.component";
import { PdfService } from "../../services/pdf.service";
import { AgencyContactInformation } from "../../models/agency-contact-info.model";
import { BuildEnvironment } from "src/environments/environment";
import { AgencyContactInfoService } from "../../services/agency-contact-info.service";
import { AgencyContactInfoComponent } from "../agency-contact-info/agency-contact-info.component";

// Create the agent data
export interface AgentData {
  agent: string;
  data: string;
  bold?: boolean;
}

// Populate table with some default data
const ELEMENT_DATA: AgentData[] = [
  { agent: "Company Name", data: "" },
  { agent: "Status", data: "", bold: true },
  { agent: "Certification Expiration Date", data: "" },
  { agent: "Authorized Countries of Recruitment", data: "" },
  { agent: "Director Region", data: "" },
  { agent: "Agency Since", data: "" },
];

@Component({
    selector: "app-my-profile",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatCardModule,
        MatTableModule,
        MatButtonModule,
        MatExpansionModule,
        AgencyContactInfoComponent,
    ],
    templateUrl: "./my-profile.component.html",
    styleUrl: "./my-profile.component.scss"
})
export class MyProfileComponent {
  user: IUser;
  hasAccess: boolean = false;
  isAdmin: boolean = false; // We can data bind to this to pass it to child components
  displayedColumns: string[] = ["agent", "data"];
  dataSource = ELEMENT_DATA;
  agencySub: Subscription;
  countries: any = [];
  agentId: number;
  oldAgencyId: string;
  agencyId: string;
  authCountryArr = [];
  isLoading: boolean = true;
  contactInfoForm!: FormGroup;
  contactInfo!: AgencyContactInformation;
  private baseurl = BuildEnvironment.GetEnvironmentUrl() + "/agency/agencycontactinfo";

  constructor(
    private authService: AuthenticationService,
    private myProfileService: MyProfileService,
    private lookupService: LookupsService,
    private pdfService: PdfService, // Injecting the PDF service
    private service: AgencyContactInfoService,
    private cdRef: ChangeDetectorRef,

  ) {}

  ngOnInit() {
    this.user = this.authService.getUserInfo();
    this.loadCountries();

    // Checking for roles
    if (
      this.authService.hasRole("Agent") ||
      this.authService.hasRole("Agency_Admin")
    ) {
      this.hasAccess = true;
      if (this.authService.hasRole("Agency_Admin")) {
        this.isAdmin = true;
      }
    } else {
      this.hasAccess = false;
    }

      this.loadTable();
  }


  createFormGroup() {
    this.contactInfoForm = new FormGroup({
      contactTitle: new FormControl(this.contactInfo.contactTitle),
      contactName: new FormControl(this.contactInfo.contactName),
      email: new FormControl(this.contactInfo.email),
      phoneCountryCode: new FormControl(this.contactInfo.phoneCountryCode),
      phone: new FormControl(this.contactInfo.phone),
      address1: new FormControl(this.contactInfo.address1),
      address2: new FormControl(this.contactInfo.address2),
      city: new FormControl(this.contactInfo.city),
      postalCode: new FormControl(this.contactInfo.postalCode),
      state: new FormControl(this.contactInfo.state),
      country: new FormControl(this.contactInfo.country),
      phoneOld: new FormControl({
        value: this.contactInfo.phoneOld,
        disabled: true,
      }),
    });
  }

  loadCountries() {
    // Use the lookup service to get Countries and Codes
    this.lookupService
      .getLookupByTableAlias("country")
      .pipe(take(1))
      .subscribe((country) => {
        this.countries = country;
      });
  }

  loadTable() {
    // Load the Agency table subscribing the getAgency Service
    console.log(this.user);
    this.agencySub = this.myProfileService
      .getAgencyInformation(this.user.oid)
      .subscribe({
        next: (agencyInfo) => {

          // only load table if agency_admin.  allows them to modify agency contact information
          if (this.isAdmin) {
            this.service
              .getAgencyContactInfo(agencyInfo.agencyId)
              .subscribe((info: AgencyContactInformation) => {
                this.contactInfo = info;
                this.createFormGroup();
              });
          }
          if (
            !agencyInfo.authCountries ||
            agencyInfo.authCountries.length === 0
          ) {
            this.authCountryArr = ["NA"];
          } else {
            this.authCountryArr = agencyInfo.authCountries.split(",");
          }
          let authCountries = [];
          this.authCountryArr.map((countryCode) => {
            if (countryCode === "NA") {
              authCountries.push("No Authorized Countries");
            }

            for (let i = 0; i < this.countries.length; ++i) {
              if (this.countries[i].Code === countryCode) {
                authCountries.push(this.countries[i].Name);
              }
            }
          });

          // Populate the table with data
          this.agencyId = agencyInfo.agencyId;
          this.oldAgencyId = agencyInfo.oldAgencyID;
          this.dataSource[0].data = agencyInfo.companyName;
          if (agencyInfo.status === "A") {
            this.dataSource[1].data = "Complete";
          } else if (agencyInfo.status === "B") {
            this.dataSource[1].data = "Probationary";
          } else {
            this.dataSource[1].data = "Undefined";
          }

          let expDate = new Date(Date.parse(agencyInfo.certExpiryDate));
          expDate.setDate(expDate.getDate() + 1);
          const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "short",
            day: "numeric",
          };
          this.dataSource[2].data = expDate.toLocaleDateString(
            undefined,
            options
          );
          this.dataSource[3].data = authCountries.join(", ");
          this.dataSource[4].data = agencyInfo.directorRegionName;
          let sinceDate = new Date(Date.parse(agencyInfo.agencySince));
          sinceDate.setDate(sinceDate.getDate() + 1);
          this.dataSource[5].data = sinceDate.toLocaleDateString(
            undefined,
            options
          );
          this.isLoading = false;
        },
        error: (error) => {
          console.log(error.message);
        },
      });
  }

  ngOnDestroy() {
    this.agencySub.unsubscribe();
  }

  // PDF Generation Method
  async onGenerateCertificate() {
    try {
      // Preparing the data
      const profileData = {
        companyName: this.dataSource[0].data,
        status: this.dataSource[1].data,
        certificationExpirationDate: this.dataSource[2].data,
        directorRegion: this.dataSource[4].data,
        agencySince: this.dataSource[5].data
      };
    
      // Generating PDF
      const pdfBytes = await this.pdfService.generateCertificate(profileData);
      // Downloading the PDF
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `Seneca_Certificate_${profileData.companyName}.pdf`;
      link.click();
    } catch (error) {
      console.error('Error generating certificate:', error);
    }
  }
}
